import React from "react";

import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField, { TextFieldProps } from "@mui/material/TextField";

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// The native picker only accepts 6-digit hex; expand #abc to #aabbcc.
const toPickerHex = (value: string): string => {
  if (!HEX_RE.test(value)) return "#000000";
  if (value.length === 7) return value;
  const [r, g, b] = [value[1], value[2], value[3]];
  return `#${r}${r}${g}${g}${b}${b}`;
};

interface ColorFieldProps extends Omit<TextFieldProps, "value" | "onChange"> {
  value: string;
  onChange: (color: string) => void;
}

/**
 * Hex color field: type the value or pick it visually. The swatch previews
 * the typed color and opens the browser's native color picker on click.
 */
export const ColorField: React.FC<ColorFieldProps> = ({
  value,
  onChange,
  ...textFieldProps
}) => {
  const isValid = HEX_RE.test(value);

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="label"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  flexShrink: 0,
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.3)",
                  bgcolor: isValid ? value : "transparent",
                  // Diagonal stripe = "no color set / invalid".
                  backgroundImage: isValid
                    ? undefined
                    : "linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.4) 55%, transparent 55%)",
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={toPickerHex(value)}
                  onChange={(e) =>
                    onChange((e.target as HTMLInputElement).value.toUpperCase())
                  }
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    padding: 0,
                    border: 0,
                    cursor: "pointer",
                  }}
                />
              </Box>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
